import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { BookPortrait } from './print.jsx';

// The real cover arriving, in four steps.
//
// Everywhere else in this theme a book is drawn rather than photographed - a
// spine is a coloured rectangle with two rules across it, and the portrait on
// this sheet is the same idea at twenty-four pixels wide. The jacket itself is
// a photograph, and cutting straight from one to the other is a photograph
// appearing on a console.
//
// So it resolves instead. The drawn portrait is held, then the cover arrives as
// a handful of coloured blocks and sharpens twice before the file itself
// replaces it - which is what a picture loading looked like on hardware that
// could not hold one, and which lands the last blocky step on exactly the grid
// the drawn portrait is on before it hands over.
//
// This is a component of its own rather than more of print.jsx because it is a
// different kind of thing: print.jsx draws, and this loads an image off the
// network, keeps a canvas and runs a clock.

// Where each step samples the cover, in blocks across and down. They double,
// and the last is 24x34 - the size the drawn portrait is, so the mosaic ends
// on the theme's own grid one step before the photograph.
const STEPS = [
  [6, 8],
  [12, 17],
  [24, 34],
];

// What the drawn portrait is drawn at, and so what the frame's width has to be
// divided by to scale it.
const PORTRAIT_W = 24;

// Half a second a step, four steps: the portrait is held for the first one,
// the three above take the next three, and the file is up at two seconds.
//
// It was a quarter of a second a step, and a second end to end - which is
// under half of even the shortest a book stays on the screen, and meant the
// one piece of animation anybody sits and watches was over before they had
// looked at it. At this speed each mosaic is on screen long enough to read as
// that jacket before the next one sharpens it, which is the whole point of
// having four of them rather than a fade.
const STEP_MS = 500;
const LAST = STEPS.length + 1;

// One step of the mosaic, drawn into a canvas that many blocks across and then
// blown back up by the stylesheet.
//
// Smoothing is left ON, which looks like the wrong call in a theme with no
// soft edges and is the right one: it is what makes each block the average of
// the piece of cover under it, so six blocks across read as that jacket rather
// than as six pixels that happened to be sampled. The blocks themselves are
// flat, and scaling them up is what `image-rendering: pixelated` is for.
function drawStep(canvas, image, blocksW, blocksH) {
  canvas.width = blocksW;
  canvas.height = blocksH;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.imageSmoothingEnabled = true;

  // Cropped to the frame the way object-fit: cover crops, so the mosaic and
  // the photograph that replaces it show the same part of the jacket and
  // nothing shifts on the last step. A cover is not always 24:34.
  const want = blocksW / blocksH;
  const have = image.naturalWidth / image.naturalHeight;
  let sx = 0;
  let sy = 0;
  let sw = image.naturalWidth;
  let sh = image.naturalHeight;
  if (have > want) {
    sw = sh * want;
    sx = (image.naturalWidth - sw) / 2;
  } else {
    sh = sw / want;
    sy = (image.naturalHeight - sh) / 2;
  }

  ctx.drawImage(image, sx, sy, sw, sh, 0, 0, blocksW, blocksH);
}

export default function CoverReveal({ book }) {
  const boxRef = useRef(null);
  const canvasRef = useRef(null);
  // 0 is the drawn portrait, 1..3 are the mosaics, 4 is the file.
  const [step, setStep] = useState(0);
  const [image, setImage] = useState(null);
  // What to multiply the drawn portrait by to fill the frame. The frame is a
  // fraction of the viewport now rather than a whole multiple of 24x34 - see
  // .eb-sheet-cover - so the one thing here that is drawn at a fixed 24x34 has
  // to be told how big that is. The mosaics and the photograph do not care:
  // they are told to fill the box and the box is whatever it is.
  const [zoom, setZoom] = useState(0);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const measure = () => setZoom(el.clientWidth / PORTRAIT_W);
    measure();
    // An observer rather than a window listener, for the same reason the level
    // uses one: the admin site renders this inside an iframe its own layout
    // resizes, and that is not a window resize.
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Loaded here rather than by rendering an <img> and waiting for onLoad,
  // because the first three steps need the picture before anything of it is
  // shown - a canvas cannot be drawn from an image that has not arrived.
  useEffect(() => {
    setStep(0);
    setImage(null);
    if (!book.coverUrl) return undefined;

    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (!cancelled) setImage(img);
    };
    // A book with no cover, a cover that 404s, a display that cannot reach the
    // image host: all of them stay on the drawn portrait, which is a real
    // answer rather than a placeholder. The classic theme puts up a grey box
    // from a third-party service for this; here there is already something
    // that belongs in the room.
    img.onerror = () => {};
    img.src = book.coverUrl;

    return () => {
      cancelled = true;
      img.onload = null;
      img.onerror = null;
    };
  }, [book.coverUrl]);

  useEffect(() => {
    if (!image) return undefined;
    const timers = [];
    for (let i = 1; i <= LAST; i++) {
      timers.push(setTimeout(() => setStep(i), i * STEP_MS));
    }
    return () => timers.forEach(clearTimeout);
  }, [image]);

  // Before the paint rather than after it. The canvas element is blank until
  // something is drawn into it, and drawing in a plain effect would put one
  // frame of empty cover between two steps.
  useLayoutEffect(() => {
    const size = STEPS[step - 1];
    if (!image || !size || !canvasRef.current) return;
    drawStep(canvasRef.current, image, size[0], size[1]);
  }, [image, step]);

  // The box is measured rather than assumed, so everything hangs off one
  // element that is always there whatever step is showing.
  const drawn = step === 0 || !image;

  return (
    <div className="eb-cover-box" ref={boxRef}>
      {drawn ? <Portrait book={book} zoom={zoom} /> : <Resolved book={book} step={step} canvasRef={canvasRef} />}
    </div>
  );
}

// The drawn cover, scaled from its own 24x34 to whatever the frame turned out
// to be. Hidden until it has been measured: scale(0) for one frame is nothing
// to look at, where an unscaled 24x34 portrait in a 200-pixel box is a visible
// stamp in the corner.
function Portrait({ book, zoom }) {
  if (!zoom) return null;
  return (
    <div className="eb-sheet-cover-art" style={{ transform: `scale(${zoom})` }}>
      <BookPortrait book={book} />
    </div>
  );
}

function Resolved({ book, step, canvasRef }) {
  if (step === LAST) {
    // No image-rendering here, unlike the steps before it. This one is being
    // drawn smaller than the file is, and nearest-neighbour on the way down is
    // a harsh, aliased picture - the opposite of the thing the last step is
    // for, which is the cover as it actually looks.
    return <img className="eb-cover-photo" src={book.coverUrl} alt="" aria-hidden="true" />;
  }

  return <canvas className="eb-cover-step" ref={canvasRef} aria-hidden="true" />;
}
